import {
  useContext,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { Link } from "@tanstack/react-router";
import NumberFlow from "@number-flow/react";
import { toast } from "sonner";
import { Check, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button as UiButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet as UiSheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  presentLineStatus,
  presentReceiptStatus,
  presentInvoiceStatus,
  TONE_CLASSES,
  TONE_EDGE,
  type StatusTone,
  type LineStatus,
  type ReceiptStatus,
  type InvoiceStatus,
} from "@shared/status";
import { formatCents } from "@shared/money";
import { daysBetween } from "@shared/time";
import { OrgSettingsContext } from "../lib/orgSettings";
import { errorMessage } from "./states";

/**
 * The app's own component layer, built on the shadcn/ui primitives in `./ui/`.
 *
 * Two layers, and the split is deliberate. `src/components/ui/*` is the shadcn
 * registry, unmodified except where a deviation is written down in the file
 * itself. This file is Nexora's: the props the screens actually call, wrapped
 * as thinly as the wrapper can be.
 *
 * **The rule for this file is: pass through, do not fight.** Every component
 * here renders a registry component with its *stock* variants and adds nothing
 * unless the addition is on the short list below. It used to do the opposite —
 * `variant={null}` to strip cva defaults, then a hand-written class string to
 * beat the base back — and the cost was that a screen could not use the kit
 * without also inheriting an argument.
 *
 * What is still custom, and why — this list should stay short:
 *
 *   1. **Status colours.** `TONE_CLASSES` / `PAYMENT_CLASSES` / the amber
 *      urgency pair. These are the client's, off their own spreadsheet, and the
 *      registry has no opinion that could replace them.
 *   2. **The teal selected chip.** A filter chip has to be readable as selected
 *      across a meeting room; shadcn's `data-[state=on]:bg-accent` is the
 *      near-white hover surface. Four classes in `Chips`.
 *   3. **390px scroll / `sm:` wrap** on that same chip row.
 *   4. **Two Radix fixes in `Sheet`** — the `pointer-events` pin and
 *      `aria-modal`, both commented at the call.
 *   5. **Furniture the registry does not ship**: the action bar, the quantity
 *      stepper, the share bar, the `--actionbar-h` measurement.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * ONE MEANING PER COLOUR. This is the whole colour system, and it is the part
 * of this file that is not a design decision:
 *
 *   red    a request line that still needs ordering — requested, approved,
 *          back-ordered — and an overdue invoice. Somebody must act.
 *   green  ordered. Money in, stock moving.
 *   gray   settled. Received, cancelled, acknowledged, draft.
 *   amber  *never a status.* Amber is urgency ("Soon" / "Urgent"), a modelled
 *          figure, and a part-paid invoice — three things that are qualifiers
 *          on a status rather than statuses themselves.
 *   teal   interaction only. It is `--primary` now, so it arrives through the
 *          registry rather than beside it. Never state.
 *
 * A status pill carries one word. Anything else the reader needs — "needs
 * ordering", "from vendor", "some missing" — is muted text *beside* the pill,
 * not a second coloured chip competing with the first.
 */

/**
 * A pill is a stock `<Badge variant="secondary">` with a status colour on it.
 *
 * `ring-1 ring-inset` is the one addition, and it is not decoration: the tone
 * strings in `@shared/status` name a `ring-*` colour, because that is where the
 * client's colour contract lives and it is not this file's to rewrite. The
 * badge's own `border-transparent` stays, so the geometry — radius, padding,
 * type size — is entirely the registry's.
 */
function Chip({
  className,
  title,
  children,
}: {
  className: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <Badge
      variant="secondary"
      title={title}
      className={cn("ring-1 ring-inset", className)}
    >
      {children}
    </Badge>
  );
}

/** The muted half of a pill: the qualifier, outside the chip. */
function withQualifier(pill: ReactNode, qualifier?: string | null) {
  if (!qualifier) return pill;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {pill}
      <span className="truncate text-xs text-muted-foreground">{qualifier}</span>
    </span>
  );
}

/**
 * The pill itself, without an opinion about where its label came from.
 *
 * `StatusPill` maps a status string through `@shared/status`; the inventory and
 * alert screens label their pills from something else entirely. Both render
 * this, so a pill is a pill on every screen.
 *
 * The qualifier is muted text beside the chip rather than a second colour
 * inside it: the colour says the state, the muted text says the detail.
 */
export function Pill({
  tone,
  label,
  qualifier,
}: {
  tone: StatusTone;
  label: ReactNode;
  qualifier?: string | null;
}) {
  return withQualifier(
    <Chip className={cn("font-semibold", TONE_CLASSES[tone])}>{label}</Chip>,
    qualifier,
  );
}

/**
 * Payment status has its own scale, and overdue is the only red one.
 *
 * Deliberately *not* routed through `TONE_CLASSES`. Under the shared tones,
 * "Sent" rendered green and "Paid" rendered grey — which reads backwards to the
 * person reconciling the month, because green is the colour their sheet uses
 * for "done" and an invoice that has merely been emailed is not done. Money
 * gets one axis instead: colourless while nothing has happened, amber part-way,
 * green when the cash is in, red when it is late.
 *
 * The wording still comes from `presentInvoiceStatus`, so a status renamed in
 * `shared/` renames here too. Exported so `kit.test.ts` can pin the contract
 * rather than keep a copy of it.
 */
export const PAYMENT_CLASSES: Record<InvoiceStatus, string> = {
  draft: "bg-gray-100 text-gray-600 ring-gray-200",
  sent: "bg-white text-gray-700 ring-gray-300",
  "partially-paid": "bg-amber-50 text-amber-900 ring-amber-200",
  paid: "bg-green-50 text-green-800 ring-green-200",
  overdue: "bg-red-50 text-red-800 ring-red-300",
};

/**
 * Status pills. Red/green/gray, matching the sheet staff read today.
 *
 * `backOrderUsesOwnColour` defaults to the org setting off `OrgSettingsContext`
 * rather than to `false`, because it *is* an org setting and a screen that
 * forgot to thread it rendered back-order in the other colour with nothing
 * failing. Pass it explicitly only when the value has to come from somewhere
 * else — the back-order report reads it off its own payload so the report and
 * its query cannot disagree.
 */
export function StatusPill({
  status,
  kind = "line",
  backOrderUsesOwnColour,
}: {
  status: string;
  kind?: "line" | "receipt" | "invoice";
  backOrderUsesOwnColour?: boolean;
}) {
  const org = useContext(OrgSettingsContext);
  const ownColour = backOrderUsesOwnColour ?? org.backOrderUsesOwnColour;

  if (kind === "invoice") {
    const invoice = status as InvoiceStatus;
    const p = presentInvoiceStatus(invoice);
    return withQualifier(
      <Chip className={cn("font-semibold", PAYMENT_CLASSES[invoice])}>{p.label}</Chip>,
      p.qualifier,
    );
  }

  const p =
    kind === "receipt"
      ? presentReceiptStatus(status as ReceiptStatus)
      : presentLineStatus(status as LineStatus, ownColour);

  return <Pill tone={p.tone} label={p.label} qualifier={p.qualifier} />;
}

/**
 * How urgent, in the requester's words. Amber for both levels, never green,
 * never red — urgency is not a status and must not be mistaken for one across
 * a room.
 *
 * The two levels differ by weight and depth rather than by hue, so "Urgent"
 * out-shouts "Soon" without adding a fourth colour to a row that already
 * carries a status pill. Routine renders nothing at all: the absence of a tag
 * is what routine looks like.
 *
 * Takes the raw stored value (`"routine" | "soon" | "urgent"`), so no screen
 * has to remember to capitalise it.
 */
const URGENCY_CLASSES = {
  urgent: "bg-amber-100 font-bold text-amber-950 ring-amber-400",
  soon: "bg-amber-50 font-medium text-amber-800 ring-amber-200",
} as const;

export function UrgencyTag({ urgency }: { urgency: string }) {
  if (urgency !== "soon" && urgency !== "urgent") return null;
  return (
    <Chip className={URGENCY_CLASSES[urgency]}>
      {urgency === "urgent" ? "Urgent" : "Soon"}
    </Chip>
  );
}

export function Money({
  cents,
  className = "",
}: {
  cents: number;
  className?: string;
}) {
  return <span className={cn("num", className)}>{formatCents(cents)}</span>;
}

/**
 * The button — a pass-through to the registry's, and nothing else.
 *
 * The vocabulary is shadcn's own. `variant` is `default | outline |
 * destructive | secondary | ghost | link` and `size` is `default | xs | sm |
 * lg | icon | icon-xs | icon-sm | icon-lg`, both handed straight to
 * `ui/button`'s cva — so a snippet copied out of the shadcn docs works here,
 * and a screen author has one dictionary to learn instead of two.
 *
 * The 44/56px tap floor is gone: the phone-critical actions — send request,
 * receive counts, approve — carry `size="lg"` explicitly.
 *
 * **Everything spreads.** The old closed prop list was flagged in two review
 * rounds as the reason call sites dropped out of the kit and hand-rolled a
 * `<button>` to get an `aria-label` or a `title`. Clerk's `SignInButton` clones
 * its child and injects a handler onto it, which only works because of that.
 *
 * `type` is the one prop that does not pass through, and it is the one genuine
 * behaviour this wrapper has over the registry's: the registry `Button` leaves
 * `type` unset, so a button dropped into a `<form>` later inherits the DOM's
 * `submit` default and reloads the page. Every form in this app submits through
 * an `onClick` that awaits a Convex mutation.
 */
export function Button({
  variant = "default",
  size = "default",
  ...rest
}: Omit<ComponentProps<typeof UiButton>, "type">) {
  return <UiButton type="button" variant={variant} size={size} {...rest} />;
}

/**
 * The quantity stepper. Two clicks, not twenty.
 *
 * Renders as a fragment, not a wrapper: the three screens that use it put the
 * controls inside their own flex row alongside an item name, and a stray div
 * between them would break every one of those layouts.
 *
 * Composed from stock `Button` (icon sizes) and stock `Input`; the registry has
 * no stepper, so this is the "furniture shadcn does not ship" case. The two
 * sizes are not the same control at two scales, which is why the markup differs:
 *
 *   `md` — a compact +/− beside a line in a list. The value is read-only text
 *          and may go to zero, because zero is how you remove a line.
 *   `lg` — the receiving screen, where a warehouse count is typed as often as
 *          it is stepped. The value is an input, clamped to 1–10,000: a
 *          delivery of zero is recorded as missing, not as a quantity.
 */
export function QtyStepper({
  value,
  onChange,
  label,
  size = "md",
}: {
  value: number;
  onChange: (next: number) => void;
  /** Names the thing being counted, for the screen reader: "One more gauze". */
  label: string;
  size?: "md" | "lg";
}) {
  if (size === "lg") {
    return (
      <>
        <UiButton
          type="button"
          variant="outline"
          size="icon-lg"
          aria-label="One fewer"
          onClick={() => onChange(Math.max(1, value - 1))}
          className="shrink-0 text-lg"
        >
          −
        </UiButton>
        <Input
          value={value}
          onChange={(event) => {
            const next = Number(event.target.value.replace(/\D/g, ""));
            onChange(Number.isFinite(next) && next > 0 ? Math.min(next, 10_000) : 1);
          }}
          inputMode="numeric"
          aria-label={label}
          className="num h-10 min-w-0 flex-1 text-center font-medium"
        />
        <UiButton
          type="button"
          variant="outline"
          size="icon-lg"
          aria-label="One more"
          onClick={() => onChange(Math.min(10_000, value + 1))}
          className="shrink-0 text-lg"
        >
          +
        </UiButton>
      </>
    );
  }

  return (
    <>
      <UiButton
        type="button"
        variant="outline"
        size="icon"
        aria-label={`One fewer ${label}`}
        onClick={() => onChange(value - 1)}
      >
        −
      </UiButton>
      <span className="num w-8 text-center text-sm font-medium">{value}</span>
      <UiButton
        type="button"
        variant="outline"
        size="icon"
        aria-label={`One more ${label}`}
        onClick={() => onChange(value + 1)}
      >
        +
      </UiButton>
    </>
  );
}

/**
 * A share-of-total bar. A bar rather than a chart: it survives 390px, a single
 * row, and a projector. No registry equivalent.
 *
 * `minPct` is the floor a visible sliver needs, and it is a prop because the
 * three reports disagree on purpose — and on whether a zero share should show
 * anything at all. Passing `minPct={value > 0 ? 3 : 0}` keeps zero empty;
 * passing a constant keeps a sliver. Neither is wrong, so neither is baked in.
 */
export function ShareBar({
  pct,
  minPct = 0,
  size = "md",
  className = "",
  role = "presentation",
  label,
}: {
  /** 0–100, already rounded by the caller. */
  pct: number;
  minPct?: number;
  size?: "sm" | "md";
  /** Margin only. The track's height and colour are not the caller's to set. */
  className?: string;
  role?: "presentation" | "img";
  /** Required by `role="img"`, meaningless without it. */
  label?: string;
}) {
  return (
    <div
      className={cn(
        size === "sm" ? "h-1.5" : "h-2 w-full",
        "overflow-hidden rounded-full bg-muted",
        className,
      )}
      role={role}
      aria-label={label}
    >
      {/* `bg-primary`, not a status colour: a share bar is a quantity, and the
          reports it lives on already carry red and green as *status*. */}
      <div
        className="h-full rounded-full bg-primary"
        style={{ width: `${Math.max(minPct, pct)}%` }}
      />
    </div>
  );
}

/**
 * One number, big, with its label under it. Stock card geometry, with the app's
 * 4px status rule on the left edge.
 *
 * `md` is the dashboard tile; `sm` is the same tile inside a report, where the
 * grid sits under a heading that has already said what the numbers are about.
 * `hero` is the single number a screen leads with, at a size that survives a
 * projector at the back of a meeting room. A screen gets at most one.
 *
 * **Tone is a stripe and a coloured number, not a wash.** A tile used to fill
 * itself red, and a dashboard reading "70 open requests" on a red panel looks
 * like seventy errors rather than a Monday. Every tone keeps the same geometry
 * (all three carry the `border-l-4`, neutral's is transparent) so a tile
 * flipping tone with live data cannot shift the grid by four pixels mid-demo.
 */
const TILE_TONES = {
  neutral: { edge: TONE_EDGE.gray, value: "text-foreground" },
  red: { edge: TONE_EDGE.red, value: "text-red-700" },
  green: { edge: TONE_EDGE.green, value: "text-green-700" },
} as const;

const TILE_SIZE = {
  sm: {
    box: "p-3",
    value: "text-2xl",
    label: "text-xs text-muted-foreground",
    hint: "text-[11px] text-muted-foreground",
  },
  md: {
    box: "p-4",
    value: "text-2xl",
    label: "mt-0.5 text-sm font-medium",
    hint: "mt-1 text-xs text-muted-foreground",
  },
  hero: {
    box: "p-5",
    value: "text-4xl sm:text-5xl",
    label: "mt-1.5 text-sm font-medium",
    hint: "mt-1 text-xs text-muted-foreground",
  },
} as const;

export function Tile({
  label,
  value,
  hint,
  tone = "neutral",
  size = "md",
  to,
  aside,
}: {
  label: string;
  value: number | string;
  hint?: ReactNode;
  /** `red` = a person must act. `green` = actively good news. Everything else
   *  is `neutral`, including a large number that is merely large. */
  tone?: "neutral" | "red" | "green";
  size?: "sm" | "md" | "hero";
  /** Makes the whole tile a link, and draws the chevron that says so. */
  to?: string;
  /** Top-right of the tile: a `<ModelledTag />`, a `<PeriodPicker />`, a
   *  comparison against last month. Mutually exclusive with `to` — the chevron
   *  a linked tile draws lives in the same corner. */
  aside?: ReactNode;
}) {
  const { edge, value: valueClass } = TILE_TONES[tone];
  const s = TILE_SIZE[size];
  const body = (
    <div
      className={cn(
        "h-full rounded-xl border border-l-4 bg-card text-card-foreground shadow-sm",
        edge,
        s.box,
        to && "transition-shadow group-hover:shadow-md",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={cn(s.value, "num font-semibold tracking-tight", valueClass)}>
          {typeof value === "number" ? <NumberFlow value={value} /> : value}
        </div>
        {aside ? <div className="shrink-0">{aside}</div> : null}
      </div>
      <div className={s.label}>{label}</div>
      {hint ? <div className={s.hint}>{hint}</div> : null}
    </div>
  );
  /* Nothing on the dashboard signalled that six of the seven cards were links,
     so a linked tile draws its own chevron. It is laid *over* the card rather
     than passed into it — `pointer-events-none` keeps every click going to the
     link underneath, and the `group` on the link is what both the chevron and
     the body's hover lift resolve against. */
  return to ? (
    <Link to={to} className="group relative block rounded-xl active:opacity-70">
      {body}
      <Chevron className="pointer-events-none absolute top-3.5 right-3" />
    </Link>
  ) : (
    body
  );
}

/** "1 item" / "2 items". The regular-plural half of the 46 hand-written
 *  ternaries; anything irregular still spells itself out. */
export const plural = (n: number, word: string): string =>
  `${word}${n === 1 ? "" : "s"}`;

/**
 * Filter chips for a set of line statuses.
 *
 * Each screen still chooses *which* statuses it offers — Requests shows the
 * whole life of a line, Approvals only the ones a manager can act on. What it
 * no longer chooses is the wording: the label comes from `presentLineStatus`,
 * so a status renamed there cannot read one way on Requests and another on
 * Approvals while both claim to be the same filter.
 */
export function statusChips<T extends LineStatus>(
  statuses: readonly T[],
  counts: Record<string, number> | undefined,
): Array<{ value: T; label: string; count?: number }> {
  return statuses.map((status) => ({
    value: status,
    label: presentLineStatus(status).label,
    count: counts?.[status],
  }));
}

/**
 * A caveat, at the size a caveat deserves.
 *
 * One quiet line under the thing it qualifies, with a hairline rule so the eye
 * can find it without being dragged to it. `caution` swaps the rule to amber
 * and is for a figure that is modelled or provisional; `neutral` is for a
 * footnote that merely explains.
 *
 * Not a box, not a border-2, not a fill. The demo rehearsal's finding was that
 * the loudest element on the best screen in the app was a disclaimer, and a
 * disclaimer that out-shouts the number it qualifies is not more honest — it
 * just makes the screen look unfinished.
 */
export function Footnote({
  children,
  tone = "neutral",
  className = "",
}: {
  children: ReactNode;
  tone?: "neutral" | "caution";
  className?: string;
}) {
  return (
    <p
      className={cn(
        "border-l-2 pl-2.5 text-xs leading-relaxed text-muted-foreground",
        tone === "caution" && "border-l-amber-300",
        className,
      )}
    >
      {children}
    </p>
  );
}

/**
 * "Modelled" — the tag that sits *on* a figure whose cost input is estimated.
 *
 * This is the load-bearing half of the modelled-cost disclosure, and the reason
 * the banner could safely become a footnote: the caveat still travels with the
 * number, on the same line, wherever that number is shown. Carson still cannot
 * point at a margin on a projector and imply it is real.
 *
 * Amber and small, in the urgency family, because "this figure is provisional"
 * is a qualifier on a number in exactly the way "urgent" is a qualifier on a
 * request. It is never red and never green: nothing here is a status.
 */
export function ModelledTag({ label = "Modelled" }: { label?: string }) {
  return (
    <Chip
      className="bg-amber-50 font-medium text-amber-900 ring-amber-200"
      title="Estimated from the supplier discount band — not a figure from an invoice"
    >
      {label}
    </Chip>
  );
}

/**
 * The modelled-cost disclosure, in one muted line.
 *
 * Same words, same facts, same un-dismissable placement above the figures; a
 * fifth of the ink of the amber box it replaced. Still zero ways for a caller
 * to soften it: no `tone`, no `compact`, no dismiss. It renders nothing only
 * when nothing is modelled.
 */
export function ModelledFootnote({
  modelled,
  total,
  bandMin,
  bandMax,
}: {
  modelled: number;
  total: number;
  bandMin: number;
  bandMax: number;
}) {
  if (modelled === 0) return null;
  const pct = total > 0 ? Math.round((modelled / total) * 100) : 0;

  return (
    <Footnote tone="caution">
      <span className="font-semibold text-foreground">Margins are modelled.</span>{" "}
      {modelled} of {total} items ({pct}%) have no supplier cost on file, so
      their cost is estimated inside your {Math.round(bandMin * 100)}–
      {Math.round(bandMax * 100)}% discount band. Clinic prices are unaffected —
      those come from the rules.
    </Footnote>
  );
}

/**
 * The page title. One `<h1>` per screen.
 *
 * shadcn's dashboard heading, verbatim: `text-2xl font-semibold tracking-tight`
 * over a `text-muted-foreground` line. Size carries the hierarchy; colour is
 * not asked to.
 *
 * `subtitle` takes a node, not a string, so a screen can put a `<Money>` in it
 * — "All clinics · $5,749.38 outstanding" was interpolating a formatted total
 * into a template literal and losing `tabular-nums` for it.
 */
export function SectionHeading({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle ? (
          <p className="text-sm leading-snug text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {/* `shrink-0`: at 390px a two-word button was being squeezed to a column
          of single letters by a long title beside it. */}
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

/**
 * The heading over a block *inside* a screen — "Below minimum", "This month".
 *
 * Muted rather than uppercase-micro now: it labels a region without competing
 * with the page title above it or the figures below it, using the registry's
 * own two text tiers instead of a private ladder.
 *
 * `as` exists for one reason: inside a report the outline is already three deep
 * — the group `<h2>`, `CardTitle`'s `<h3>` for the report itself — so a heading
 * over a block *within* a report is an `<h4>`. Same treatment, correct level;
 * the reports used to carry their own copy of this component to get it.
 */
export function SubHeading({
  children,
  className = "",
  as: Tag = "h2",
}: {
  children: ReactNode;
  className?: string;
  as?: "h2" | "h3" | "h4";
}) {
  return (
    <Tag className={cn("text-sm font-medium text-muted-foreground", className)}>
      {children}
    </Tag>
  );
}

/**
 * Empty is not a filter.
 *
 * A single-select toggle group lets you click the selected item to deselect,
 * handing back `""`. A filter with no value is a screen with no list, so the
 * current value is re-emitted instead — which is *exactly* what the row of
 * hand-rolled buttons did before Radix, and Dispatch depends on the re-emission:
 * it is how "who is delivering" clears itself back to nobody.
 *
 * Exported because it is the one piece of `Chips` worth a test.
 */
export function keepSelection<T extends string>(next: string, current: T): T {
  return (next === "" ? current : next) as T;
}

/**
 * Filter chips with counts — shadcn's `ToggleGroup`, stock `outline` variant.
 *
 * Radix owns the parts that used to be hand-rolled: a single-select group is a
 * radio group, so the items carry `role="radio"` / `aria-checked` and arrow keys
 * walk the row instead of tab visiting five filters one at a time.
 *
 * The registry's segmented geometry is kept as shipped — joined edges, shared
 * radius, `size="lg"` because this is the control a dental assistant taps most
 * on a phone. Four classes are added and every one is on the short list at the
 * top of this file:
 *
 *   `flex-none`                     the items size to their words rather than
 *                                   sharing the width, which is what lets the
 *                                   row overflow and scroll at 390px.
 *   `data-[state=on]:bg-primary`    shadcn's `data-[state=on]:bg-accent` is the
 *   `…:text-primary-foreground`     near-white *hover surface* token (see
 *   `…:hover:bg-primary`            index.css), so a selected chip would render
 *                                   white on white. Same modifier group, so
 *                                   tailwind-merge replaces the registry's
 *                                   outright rather than out-specifying it.
 *
 * **390px scrolls; desktop wraps.** Six chips do not fit on a phone, and
 * wrapping them into three rows pushes the list itself off the screen — so the
 * row scrolls, bleeding to both edges (`-mx-4 px-4`) so the cut-off chip reads
 * as "there is more" rather than as a broken layout. From `sm:` up there is
 * room, so it wraps instead.
 */
export function Chips<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string; count?: number }>;
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:overflow-x-visible sm:px-0">
      <ToggleGroup
        type="single"
        variant="outline"
        size="lg"
        value={value}
        onValueChange={(next: string) => onChange(keepSelection(next, value))}
        className="w-auto sm:flex-wrap"
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className="flex-none data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:hover:bg-primary"
          >
            {option.label}
            {option.count !== undefined ? (
              <span className={value === option.value ? "opacity-70" : "text-muted-foreground"}>
                {" "}
                {option.count}
              </span>
            ) : null}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

/**
 * The "this opens" affordance, and the only place the chevron is drawn.
 *
 * A row that expands into a detail card, a `<Tile to>`, a `CollapsibleSection`
 * header — all three used to hand-write their own `<ChevronRight>` and drifted
 * on the colour. The rule lives here now: muted until hovered, resolving
 * against the *caller's* `group`, which means the caller must put `group` on
 * the row and not on this. Callers append their own positioning and rotation;
 * `cn` lets those win over the defaults.
 */
export function Chevron({ className = "" }: { className?: string }) {
  return (
    <ChevronRight
      aria-hidden="true"
      className={cn(
        "size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground",
        className,
      )}
    />
  );
}

/**
 * A row you tick, in a list you tick several of.
 *
 * Approvals and Dispatch are the two screens a manager moves between in the
 * approve → dispatch chain, and each had grown its own copy of this control.
 * The copies disagreed on three things, so the same action looked like two
 * different controls one tap apart, in the demo itself.
 *
 * Selected is the registry's own selected-row convention (`bg-muted`, the same
 * token `<TableRow data-state="selected">` uses); the tick box is the registry
 * checkbox's checked look (`border-primary bg-primary text-primary-foreground`).
 * The hit area is the row, not the box, because a thumb aiming at a small
 * square on a moving list misses.
 *
 * **The row is still the `<button aria-pressed>` it always was**, and that is
 * load-bearing: `tools/demo.mjs` drives approvals and dispatch by pressing
 * these rows.
 *
 * Which is why the tick is *not* the registry's `<Checkbox>`, and why that
 * component is not installed at all. Radix's checkbox root is a `<button
 * role="checkbox">`. Putting one inside this row's button is invalid markup
 * that React reports as `validateDOMNesting: <button> cannot appear as a
 * descendant of <button>` — a console error on the demo path, which the
 * done-bar forbids. `asChild` does not rescue it either: shadcn's wrapper
 * renders the Indicator as explicit JSX children, so an `asChild` child is
 * discarded. So the box is a `<span aria-hidden>` and the tick is lucide's
 * `Check`, the same icon a checkbox would draw.
 */
export function SelectableRow({
  selected,
  onToggle,
  children,
}: {
  selected: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onToggle}
      className={cn(
        "flex w-full items-start gap-3 p-3 text-left text-sm transition-colors",
        selected ? "bg-muted" : "hover:bg-muted/50",
      )}
    >
      <span
        aria-hidden
        data-state={selected ? "checked" : "unchecked"}
        className={cn(
          "mt-0.5 grid size-4 shrink-0 place-content-center rounded-[4px] border shadow-xs transition-colors",
          selected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-input bg-background",
        )}
      >
        {selected ? <Check className="size-3.5" /> : null}
      </span>
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );
}

/**
 * The bar that parks a screen's decision above the bottom nav. No registry
 * equivalent — this is app furniture.
 *
 * `bottom-16` clears the phone's tab bar; `lg:bottom-0` drops it to the floor
 * once that bar is gone. `z-30` sits under the desktop sidebar (`z-40`) so a
 * pick list's footer cannot run over the navigation. `pb-safe` is
 * unconditional — a home indicator eats the bottom 34px of a phone whether or
 * not the screen that drew the bar remembered.
 *
 * The inner column is the shell's content column, repeated: `max-w-3xl` on a
 * phone, `max-w-6xl` past `lg`, offset by the nav rail. Without it the buttons
 * float in the middle of a 1280px screen while the rows they act on are pinned
 * left, which reads as two unrelated screens.
 *
 * **The bar reserves its own room.** It measures itself and publishes the
 * result as `--actionbar-h` on the document element; the shell's `<main>`
 * pads by that variable (see `.app-main` in `index.css`). No screen picks a
 * clearance number, because picking one is what caused the buried-control
 * defect: the bar grew a confirmation strip, the hard-coded padding did not,
 * and the one control that lets an assistant say "before Friday" ended up
 * underneath it — rendered, enabled, and untappable.
 */
export function ActionBar({
  children,
  className = "",
  flowAt,
}: {
  children: ReactNode;
  /** Layout *inside* the content column — `space-y-2` and the like. The bar's
   *  own geometry is not the caller's to set. */
  className?: string;
  /** `"lg"` drops the bar into normal flow past 1024px, for a screen whose
   *  desktop layout already keeps the decision on screen (Request's sticky
   *  basket column). In flow it reserves its own space, so it publishes a
   *  height of 0. */
  flowAt?: "lg";
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = ref.current;
    const root = document.documentElement;
    if (!bar) return;

    const wide = flowAt === "lg" ? window.matchMedia("(min-width: 1024px)") : null;
    /* `offsetHeight` inside a ResizeObserver callback is a layout read the
       browser has already done — the observer fires after layout, so this
       does not force a second one. Writing to a custom property rather than
       to React state is deliberate: the height changes whenever a long item
       name wraps the confirmation strip to a second line, and routing that
       through state would re-render the whole 97-row palette behind it. */
    const measure = () => {
      const h = wide?.matches ? 0 : bar.offsetHeight;
      root.style.setProperty("--actionbar-h", `${h}px`);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    wide?.addEventListener("change", measure);
    return () => {
      observer.disconnect();
      wide?.removeEventListener("change", measure);
      /* The bar unmounts with the selection it acts on. Leaving the variable
         behind would strand 140px of dead space under every later screen. */
      root.style.removeProperty("--actionbar-h");
    };
  }, [flowAt]);

  const flows = flowAt === "lg";

  return (
    /* One separator, not two. It carried a border *and* an upward shadow, and
       the bottom tab bar below it carries a border of its own — three lines of
       chrome stacked in 80px at 390px. The border is the one that survives. */
    <div
      ref={ref}
      className={cn(
        "pb-safe fixed inset-x-0 bottom-16 z-30 border-t bg-background/95 p-3 backdrop-blur",
        flows
          ? "lg:static lg:z-auto lg:mt-3 lg:shrink-0 lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none"
          : "lg:bottom-0 lg:pl-[calc(var(--nav-rail)+1.5rem)]",
      )}
    >
      <div
        className={cn(
          "mx-auto max-w-3xl",
          flows ? "lg:max-w-none" : "lg:max-w-6xl lg:px-8",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The billing tables' shared grid geometry.
 *
 * The track widths are not here on purpose. Tailwind's scanner reads class
 * strings literally, so a `grid-cols-[...]` assembled from a variable produces
 * no CSS at all — each screen keeps its four whole strings where the scanner
 * can see them, and this owns only the part that is genuinely the same.
 */
const GRID = "lg:grid lg:items-center lg:gap-x-3";

/**
 * Which of a table's four column templates applies.
 *
 * The two axes are the two columns that are not always there: a clinic manager
 * sees one clinic, so a Clinic column repeating that clinic's name seventeen
 * times is a column of noise, and Margin follows the payload rather than the
 * role. Invoices and Statements each carried their own copy of this, and both
 * built a `"clinic margin"` lookup key by concatenating strings and then cast
 * the result back into the object — two ways for a typo to compile.
 */
export function columnsFor(
  templates: {
    clinicMargin: string;
    clinic: string;
    margin: string;
    plain: string;
  },
  showClinic: boolean,
  showMargin: boolean,
): string {
  const template = showClinic
    ? showMargin
      ? templates.clinicMargin
      : templates.clinic
    : showMargin
      ? templates.margin
      : templates.plain;
  return `${GRID} ${template}`;
}

/** The registry `<Label>`'s own typography, for the handful of places that put
 *  it on a `<span>` rather than a `<Label>`. */
export const FIELD_LABEL = "text-sm font-medium";

export const relativeDay = (ms: number): string => {
  // `daysBetween` already floors and clamps, which is the same "a future
  // timestamp reads as today" rule this used to open-code.
  const days = daysBetween(ms, Date.now());
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "last week";
  return `${Math.floor(days / 7)} weeks ago`;
};

/** "Mar 4". One formatter, built once: `toLocaleDateString` constructed a fresh
 *  `Intl.DateTimeFormat` on every call, and an inventory table calls it once a
 *  row. Same locale, same options, same output. */
const SHORT_DATE = new Intl.DateTimeFormat("en-CA", {
  month: "short",
  day: "numeric",
});

export const shortDate = (ms: number): string => SHORT_DATE.format(ms);

/* -------------------------------------------------------------------------- */
/* Grouping                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * A named group of rows that can be folded away.
 *
 * The two screens that need it are the two long ones: Inventory, where ninety-
 * seven items arrive at 390px as one undifferentiated wall, and Dispatch, where
 * the unit of work is a clinic rather than a line. Both want the same object —
 * a header that says what the group is, how many are in it, and what state it
 * is in, with the rows underneath.
 *
 * Three rules it enforces so twelve section headers cannot become twelve
 * designs:
 *
 *   - The header is a full-width button. On a phone the target is the row, not
 *     the chevron.
 *   - The count is the only figure in the header. Everything else — "4 below
 *     minimum", "2 urgent" — is `summary`, muted, and must not be a coloured
 *     pill: a header full of chips is a wall again, just a louder one.
 *   - `tone` draws the same 4px left rule the cards and tiles use, so a section
 *     containing something red announces it before it is opened.
 *
 * Closed sections unmount their children rather than hiding them. On Inventory
 * that is the difference between rendering ninety-seven rows and rendering
 * eight, and a fold animation on a list that long reads as lag.
 *
 * `children` may be a **thunk**, and on a long list it should be. Passed as
 * elements, all ninety-seven rows are constructed on every render and then
 * thrown away by the closed sections. Passed as `() => …`, a closed section
 * costs nothing at all.
 *
 * Uncontrolled by default. Pass `open` *and* `onOpenChange` for an expand-all
 * control; pass neither and it keeps its own state from `defaultOpen`.
 */
export function CollapsibleSection({
  title,
  count,
  summary,
  tone = "gray",
  defaultOpen = true,
  open,
  onOpenChange,
  sticky = true,
  stickyTop = 0,
  children,
}: {
  title: string;
  /** How many rows are inside. Rendered tabular, beside the title. */
  count?: number;
  /** One muted line: "4 below minimum · 2 expiring". Never a pill. */
  summary?: ReactNode;
  /** The left rule. `gray` (the default) draws a transparent one, so a settled
   *  section is not competing for attention. */
  tone?: StatusTone;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The header stays put while its rows scroll. Turn off inside a container
   *  that is itself scrolling. */
  sticky?: boolean;
  /** Clearance for the app header — pass `useHeaderOffset()`. */
  stickyTop?: number;
  /** Elements, or a thunk that builds them only when the section is open. */
  children: ReactNode | (() => ReactNode);
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const isOpen = open ?? uncontrolled;

  const toggle = (next: boolean) => {
    if (open === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={toggle}
      asChild
      /* No `overflow-hidden`: it would make this box the sticky header's
         containing block and the header would stop sticking. */
      className={cn(
        "rounded-xl border border-l-4 bg-card text-card-foreground shadow-sm",
        TONE_EDGE[tone],
      )}
    >
      <section>
        <h2
          /* Sticky on the heading, not on the button. A sticky button inside a
             heading that is exactly button-height has nowhere to travel. */
          className={sticky ? "sticky z-10" : ""}
          style={sticky ? { top: stickyTop } : undefined}
        >
          {/* Radix owns `aria-expanded` and the `aria-controls`/`id` pairing
              now, which is the point of moving to it — those were hand-written
              and the panel's `id` had to be threaded through a `useId`. */}
          <CollapsibleTrigger
            type="button"
            className="group flex w-full items-center gap-3 rounded-t-xl bg-background/95 px-3 py-2 text-left backdrop-blur transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <span className="min-w-0 flex-1">
              <span className="flex items-baseline gap-2">
                <span className="truncate text-sm font-medium">{title}</span>
                {count === undefined ? null : (
                  <span className="num shrink-0 text-xs text-muted-foreground">
                    {count}
                  </span>
                )}
              </span>
              {summary ? (
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                  {summary}
                </span>
              ) : null}
            </span>
            <Chevron
              className={cn(
                "transition-[color,transform] duration-150",
                isOpen && "rotate-90",
              )}
            />
          </CollapsibleTrigger>
        </h2>
        <CollapsibleContent className="panel-in border-t">
          {/* The thunk, still a thunk. Radix unmounts a closed panel's
              children, but that is not the saving this exists for: passed as
              elements, all ninety-seven inventory rows are *constructed* on
              every render of the parent and then thrown away unrendered. The
              `isOpen` guard is what keeps a folded section costing nothing, and
              it must stay outside `<CollapsibleContent>` — putting the call
              inside it would still evaluate during the parent's render. */}
          {isOpen ? (typeof children === "function" ? children() : children) : null}
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

/* -------------------------------------------------------------------------- */
/* Sheet                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The bottom sheet — stock `ui/sheet`, `side="bottom"`.
 *
 * It exists because of one specific failure: option panels — urgency, a note, a
 * receipt outcome — expanded *inline*, downward, behind the screen's own fixed
 * action bar, so on a 390px phone the control you had just revealed was under
 * the button you were about to press. A sheet takes the decision off the page
 * and puts it above everything, including the tab bar, which is also what stops
 * a half-made decision being abandoned by a stray tap on "Inventory".
 *
 * Radix owns the modal contract: focus restore, the `Tab` cycle, Escape, the
 * scroll lock, inert background content. The API is unchanged — `open` /
 * `onClose` / `title` / `description` / `footer` / `children`, controlled from
 * the calling screen exactly as before — and the close button, the header
 * paddings and the panel's own styling are now the registry's.
 *
 * Two additions survive, both Radix bugs rather than taste, both commented at
 * the call: the `pointer-events` pin and `aria-modal`. `max-h-[85dvh]` plus a
 * scrolling body is the third: a sheet listing every clinic has to stop growing
 * somewhere.
 */
export function Sheet({
  open,
  onClose,
  title,
  description,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** Names the decision — "How urgent?", "What arrived?". Required: it is the
   *  dialog's accessible name, and a sheet with no title is a panel that
   *  appeared for no stated reason. */
  title: string;
  /** One quiet line under the title. */
  description?: ReactNode;
  /** The confirm/cancel row. Pinned below the scrolling body and above the
   *  home indicator, so a long list of options cannot push it off screen. */
  footer?: ReactNode;
  children: ReactNode;
}) {
  /**
   * Radix points `aria-describedby` at a description element it expects to
   * exist, and logs a console warning when it does not. `description` is
   * optional here, so on the sheets that omit it the attribute is removed
   * outright — the spread lands after Radix's own default inside
   * `DialogContentImpl`, which is what lets an explicit `undefined` win. A
   * console warning on the demo path is a failed done-bar item, not a nit.
   */
  const describedBy: { "aria-describedby"?: undefined } = description
    ? {}
    : { "aria-describedby": undefined };

  return (
    <UiSheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="bottom"
        {...describedBy}
        /**
         * The panel is never inert. Radix computes an inline `pointer-events`
         * from its layer bookkeeping, and on roughly one open in ten — when a
         * previous sheet's node has not left the module-level `layers` set
         * before this one's is added — it lands on `none`. That renders a
         * sheet that is fully visible and accepts nothing: no urgency button,
         * no note field, no Done. Radix spreads `props.style` *after* its own
         * in `DismissableLayerImpl`, so this wins outright, and it is only
         * ever correct here — this app shows one sheet at a time, so the sheet
         * on screen is always the layer that should be taking input.
         */
        style={{ pointerEvents: "auto" }}
        /**
         * Kept from the hand-rolled version, and not redundant.
         *
         * Radix does not set `aria-modal`; it conveys modality by marking
         * every other child of `<body>` `aria-hidden` through the `aria-hidden`
         * package. Measured here, that misses `#root` — the focus guards,
         * Clerk's container, the toaster and the scrim are all marked, the
         * application itself is not — so without this attribute a screen
         * reader could still browse the page behind an open sheet. One prop,
         * and it is the attribute assistive tech actually acts on.
         */
        aria-modal="true"
        className="sheet-rise max-h-[85dvh]"
      >
        <SheetHeader className="shrink-0 pr-10 pb-0">
          <SheetTitle>{title}</SheetTitle>
          {description ? <SheetDescription>{description}</SheetDescription> : null}
        </SheetHeader>
        <div
          className={cn("min-h-0 flex-1 overflow-y-auto px-4", !footer && "pb-safe")}
        >
          {children}
        </div>
        {footer ? (
          <div className="pb-safe mt-auto shrink-0 border-t px-4 pt-4">{footer}</div>
        ) : null}
      </SheetContent>
    </UiSheet>
  );
}

/* -------------------------------------------------------------------------- */
/* Periods                                                                     */
/* -------------------------------------------------------------------------- */

const MONTH_LABEL = new Intl.DateTimeFormat("en-CA", {
  month: "long",
  year: "numeric",
});

/** `"2026-07"` → `"July 2026"`. */
export const monthLabel = (key: string): string => {
  const [year, month] = key.split("-").map(Number);
  return MONTH_LABEL.format(new Date(year, month - 1, 1));
};

/**
 * Which month a screen is looking at — stock `ui/select`.
 *
 * It was a native `<select>`, on the argument that iOS draws a better wheel
 * than any popover could at 390px. That argument is overridden: the app is
 * standardising on the registry, and a lone unstyled platform control beside
 * shadcn triggers is the kind of seam a client notices. Radix's listbox is
 * keyboard- and touch-driven, portals above the sheet layer, and matches every
 * other control on the screen.
 *
 * `label` is still the accessible name and is still visually hidden — the
 * selected month is the visible label.
 */
export function PeriodPicker({
  value,
  onChange,
  options,
  label = "Period",
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  /** The accessible name. Visually hidden — the selected month is the label. */
  label?: string;
  className?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className={className}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* -------------------------------------------------------------------------- */
/* Toasts                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Confirmations, in the user's words.
 *
 * The standard, and it is two halves:
 *
 *   **[what happened, as the person would say it] + [what happens next]**
 *
 * "1 line updated" is neither. It is the mutation's return value read aloud —
 * the reader has to translate "line" back into "the gauze I asked for" and is
 * told nothing about what to do now. The rewritten shape is
 * `notify.done("2 items ordered from the warehouse", "They'll show on the W68
 * dispatch list.")`.
 *
 * Rules that fall out of that:
 *
 *   - Name the *thing*, not the row. Items, deliveries, invoices, clinics —
 *     never "lines", "records" or "entities".
 *   - The second half is a sentence, not a fragment, and says where the work
 *     went. If nothing happens next, leave it out rather than padding it.
 *   - Failures say what could not be done, in the same nouns, and let the
 *     server's own sentence be the detail — `notify.failed` does that for you.
 *   - No jargon, no ids, no counts of internal objects.
 *
 * Three levels, because Sonner's six invite a decision nobody should be making
 * per call site: it went through, it went through with something to notice, it
 * did not go through.
 */
export const notify = {
  /** It went through. */
  done(happened: string, next?: string) {
    toast.success(happened, next ? { description: next } : undefined);
  },
  /** It went through, and there is something the reader must notice — a short
   *  delivery, a discrepancy, a partial. */
  check(happened: string, next?: string) {
    toast.warning(happened, next ? { description: next } : undefined);
  },
  /** It did not go through. `couldNot` is written as "Could not send the
   *  request"; the server's own refusal becomes the description. */
  failed(couldNot: string, error: unknown) {
    toast.error(couldNot, { description: errorMessage(error) });
  },
};
